import React, { useCallback, useState, useRef, createContext, useContext } from 'react';
export type ToastType = 'success' | 'error' | 'warning' | 'info';
export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}
interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const dismissToast = useCallback((id: string) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const duration = toast.duration ?? 3500;
    setToasts(prev => {
      // Max 5 toasts at once
      const trimmed = prev.length >= 5 ? prev.slice(-4) : prev;
      return [...trimmed, {
        ...toast,
        id
      }];
    });
    timersRef.current[id] = setTimeout(() => {
      dismissToast(id);
    }, duration);
  }, [dismissToast]);
  return <ToastContext.Provider value={{
    toasts,
    showToast,
    dismissToast
  }} data-id="element-5584">
      
      {children}
    </ToastContext.Provider>;
}
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}